import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { GoogleButton } from '@/components/GoogleButton';
import { ErrorText, FullScreenSpinner } from '@/components/ui';
import { errorMessage } from '@/lib/api';
import { authClient, authErrorMessage } from '@/lib/auth-client';
import { invitesApi, keys, useInvite, useMe } from '@/lib/queries';
import { useClearUrlSecret } from '@/lib/url-secrets';
import { AuthLayout } from './auth/AuthLayout';

const ACTIVE_KEY = 'strl.activeProperty';

/** The page behind an invite email: who asked, which property, and a way in. */
export function Invite() {
  const { token = '' } = useParams();
  const invite = useInvite(token);
  // The page keeps the token from the router; the address bar shows /invite.
  useClearUrlSecret('/invite');
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const here = `/invite/${token}`;

  if (invite.isPending || me.isPending) return <FullScreenSpinner />;
  if (invite.isError || !invite.data)
    return (
      <AuthLayout title="This link is no longer valid." sub="Ask the host to send a new invite.">
        <Link to="/login" className="btn btn-outline hv">
          Log in
        </Link>
      </AuthLayout>
    );
  const inv = invite.data;
  const user = me.data?.user ?? null;
  const sameAddress = !!user && user.email.toLowerCase() === inv.email;

  const join = async () => {
    setBusy(true);
    setError(null);
    try {
      const property = await invitesApi.accept(token);
      try {
        localStorage.setItem(ACTIVE_KEY, property.id);
      } catch {
        /* private mode */
      }
      await qc.invalidateQueries({ queryKey: keys.me });
      void navigate('/app', { replace: true, state: { toast: `You are on ${property.name}` } });
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };

  const signUpAndJoin = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const res = await authClient.signUp.email({
      name: name.trim() || inv.email,
      email: inv.email,
      password,
      callbackURL: here,
    });
    if (res.error) {
      setError(authErrorMessage(res.error));
      setBusy(false);
      return;
    }
    await qc.invalidateQueries({ queryKey: keys.me });
    await join();
  };

  const title =
    inv.state === 'open'
      ? `${inv.invitedBy} invited you to ${inv.propertyName}.`
      : inv.state === 'accepted'
        ? 'This invite has already been used.'
        : 'This invite has expired.';
  const sub =
    inv.state === 'open'
      ? 'Same posts, calendar, media and channels. Join with the address the invite went to.'
      : inv.state === 'accepted'
        ? 'Log in to see the property.'
        : 'Ask the host to send a new one.';

  return (
    <AuthLayout title={title} sub={sub}>
      {inv.state !== 'open' ? (
        <Link to="/login" className="btn btn-outline hv">
          Log in
        </Link>
      ) : user ? (
        sameAddress ? (
          <>
            <button
              type="button"
              className="btn btn-primary btn-lg hv"
              disabled={busy}
              onClick={() => void join()}
            >
              {busy ? 'Joining…' : `Join as ${user.email}`}
            </button>
            <ErrorText>{error}</ErrorText>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: '#555', lineHeight: 1.5 }}>
              You are logged in as {user.email}, but this invite went to {inv.email}. Log out and
              come back with that address.
            </p>
            <button
              type="button"
              className="btn btn-outline hv"
              onClick={() =>
                void authClient.signOut().then(() => qc.invalidateQueries({ queryKey: keys.me }))
              }
            >
              Log out
            </button>
          </div>
        )
      ) : (
        <form onSubmit={signUpAndJoin} style={{ display: 'contents' }}>
          <GoogleButton
            enabled={!!me.data?.features.googleAuth}
            callbackURL={here}
            label={`Continue with Google as ${inv.email}`}
          />
          <div className="divider">or create a password</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <input className="input" value={inv.email} readOnly aria-label="Email" />
            <input
              className="input"
              placeholder="Your name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Name"
              required
            />
            <input
              className="input"
              type="password"
              placeholder="Password (8+ characters)"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-label="Password"
              minLength={8}
              required
            />
          </div>
          <ErrorText>{error}</ErrorText>
          <button type="submit" className="btn btn-primary btn-lg hv" disabled={busy}>
            {busy ? 'Joining…' : 'Create account and join'}
          </button>
          <span style={{ fontSize: 12.5, color: '#777' }}>
            Already have an account?{' '}
            <Link to="/login" state={{ from: here }} style={{ fontWeight: 600 }}>
              Log in
            </Link>
          </span>
        </form>
      )}
    </AuthLayout>
  );
}
